import { BASE_COLORS, type BaseColorPreset } from '../lib/shadcn-presets'
import type { DesignSystemState } from '../lib/design-system-options'
import type { SourceTokenRow } from '../../openpencil-figma/openpencil-source-library-model'
import { KO_DESIGN_AXES_COPY, type DesignAxesCopy } from './axes-copy'
import type { AxisName, AxisResolution, SourceEvidence } from './types'
import { deltaE76, parseColorToRgb, rgbToLab, type Rgb } from '@cogo/source-openpencil'

const AXES: AxisName[] = ['style', 'baseColor', 'theme', 'chart', 'radius', 'font']

/** 소스 변수 이름이 이 축의 재료인가. `SOURCE_TOKEN_SEMANTIC_ROLES` 어휘를 따른다. */
const AXIS_MATCHERS: Record<AxisName, RegExp | null> = {
  style: null,                       // 소스에서 유도할 수 없다 — 항상 gap
  baseColor: /(^|\/)(primary|brand|base|neutral)(\/|$)/i,
  theme: /(^|\/)(theme|accent)(\/|$)/i,
  chart: /(^|\/)(chart|graph|series)(\/|$)/i,
  radius: /(^|\/)(radius|corner|rounded)(\/|$)/i,
  font: /(^|\/)(font|typeface|family)(\/|$)/i,
}

function gap(axis: AxisName, why: string): AxisResolution {
  return { axis, value: null, origin: 'gap', explanation: why, advice: [] }
}

function evidenceOf(token: SourceTokenRow): SourceEvidence {
  return {
    variablePath: token.name,
    // `swatch` wins when present: it is the field the real SourceTokenRow
    // JSDoc reserves for "a renderable colour" (regex-validated hex), so a
    // colour token's raw evidence should be its swatch, not `displayValue`
    // (a generic stringification used for non-colour tokens like radius).
    rawValue: token.swatch ?? token.displayValue ?? '',
    collection: token.collectionName,
  }
}

/**
 * `BASE_COLORS` entries (`../lib/shadcn-presets` → `@cogo/design-presets`)
 * carry no flat `hex` field — each is `{ name, light, dark }` where
 * `light`/`dark` are `Record<string, string>` of oklch() CSS strings
 * (verified directly against `packages/cogo-design-presets/src/shadcn-presets.ts`,
 * see task-1-report.md). `light['muted-foreground']` is the representative
 * swatch: `background`/`foreground`/`primary` sit near-white/near-black with
 * ~0 chroma for every base color (indistinguishable from each other), while
 * `muted-foreground` sits at a shared mid lightness (~0.55) carrying each
 * family's actual hue — that's what separates "stone" from "zinc" from
 * "mauve". Confirmed empirically: for `#78716C` (Tailwind stone-500), this
 * field ranks `stone` nearest by a wide margin under both raw RGB distance
 * and CIE76 ΔE (see task-1-report.md review-round-1 section).
 *
 * Parsing goes through `@cogo/source-openpencil`'s `parseColorToRgb` — the
 * canonical hex6/hex8/oklch parser already used by F6 visual-conformance
 * (`packages/cogo-source-openpencil/src/f6-visual-conformance.ts`) — instead
 * of a hand-rolled duplicate, so hex8 (alpha) values parse correctly instead
 * of silently failing.
 */
function baseColorSwatchRgb(candidate: BaseColorPreset): Rgb | null {
  return parseColorToRgb(candidate.light['muted-foreground'] ?? '')
}

/**
 * 소스 색을 가장 가까운 BASE_COLORS 항목으로 접는다. 거리는 CIE76 ΔE (Lab
 * 공간) — raw RGB 유클리드 거리보다 지각적으로 더 균일하다
 * (`@cogo/source-openpencil`'s `deltaE76`/`rgbToLab`, F6와 동일 함수).
 *
 * 해석할 수 없는 값(`parseColorToRgb` 가 null)은 **`BASE_COLORS[0]`로 조용히
 * 폴백하지 않고 `null` 을 반환한다** — 호출자(`deriveAxes`)가 이걸 `gap` 으로
 * 표시해야 "origin: 'from-source'인데 실제로는 소스와 무관한 기본값" 이라는
 * 거짓 상태를 피할 수 있다.
 */
function nearestBaseColor(hex: string): string | null {
  const target = parseColorToRgb(hex)
  if (!target) return null
  const targetLab = rgbToLab(target)
  let best: string | null = null
  let bestDistance = Number.POSITIVE_INFINITY
  for (const candidate of BASE_COLORS) {
    const rgb = baseColorSwatchRgb(candidate)
    if (!rgb) continue
    const distance = deltaE76(targetLab, rgbToLab(rgb))
    if (distance < bestDistance) {
      bestDistance = distance
      best = candidate.name
    }
  }
  return best
}

export function deriveAxes(
  tokens: SourceTokenRow[],
  copy: DesignAxesCopy = KO_DESIGN_AXES_COPY,
): Record<AxisName, AxisResolution> {
  const axes = {} as Record<AxisName, AxisResolution>

  for (const axis of AXES) {
    const matcher = AXIS_MATCHERS[axis]
    if (!matcher) {
      axes[axis] = gap(axis, copy.gapCannotRead)
      continue
    }
    const hit = tokens.find((token) => matcher.test(token.name))
    if (!hit) {
      axes[axis] = gap(axis, copy.gapNoValueInSource)
      continue
    }
    const evidence = evidenceOf(hit)

    if (axis === 'baseColor') {
      const matched = nearestBaseColor(evidence.rawValue)
      if (matched === null) {
        // 이름은 baseColor 재료(primary/brand/base/neutral)처럼 매치됐지만
        // 값 자체를 색으로 해석하지 못했다 — from-source 라고 거짓 표시하지
        // 않고 gap 으로 남긴다. evidence 는 유지한다: "못 찾음"과 "찾았지만
        // 못 읽음"은 사용자에게 다른 정보다.
        axes[axis] = {
          axis,
          value: null,
          origin: 'gap',
          explanation: copy.gapBaseColorUnparsable(evidence.variablePath, evidence.rawValue),
          evidence,
          advice: [],
        }
      } else {
        axes[axis] = {
          axis,
          value: matched,
          origin: 'from-source',
          explanation: copy.fromSource(evidence.variablePath),
          evidence,
          advice: [],
        }
      }
      continue
    }

    // Same `copy.fromSource` the baseColor branch above already used. This
    // one branch authored the sentence inline instead, so every non-baseColor
    // axis that DID resolve from a source printed Korean on an English
    // screen — the one sentence on this surface that only appears once real
    // source tokens are present, which is why the axis-list tests (all of
    // which hand the component a hand-written `explanation`) never saw it.
    axes[axis] = {
      axis,
      value: hit.shortName,
      origin: 'from-source',
      explanation: copy.fromSource(evidence.variablePath),
      evidence,
      advice: [],
    }
  }

  return axes
}

export type { DesignSystemState }
